import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Question, QuestionInfo } from "@/services/generatedAPIs.schemas"
import { useTranslation } from "react-i18next"
import { getQuestionText, getStandardAnswerText } from "@/lib/utils"
import { Printer } from "lucide-react"
import { useEffect, useRef } from "react"
import { ReadonlyQuestionDisplay } from "@/components/readonly-question-display"
import { convertSpacesToLatex, processBackslashes } from "@/utils/preprocess"
import { format } from "date-fns"
import { parseMultipleChoiceOptions } from "@/utils/mcq-utils"

// Declare the math-field element type
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'math-field': any;
    }
  }
}

export enum PreviewMode {
  QUIZ,
  HOMEWORK
}

interface HomeworkPreviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  questions: QuestionInfo[]
  answers?: Question[]
  mode: PreviewMode
  title: string
  name?: string
  description?: string
  subject?: string
  unit?: string
  className?: string
  dueDate?: Date
  onConfirm?: () => void
  isLoading?: boolean
  language?: string
  /** 覆盖确认按钮样式（学生提交流程用黄色 #FFD57B，与设计稿一致；教师端不传，保持默认主题色） */
  confirmClassName?: string
}

export function HomeworkPreviewDialog({
  open,
  onOpenChange,
  questions,
  answers,
  mode,
  title,
  name,
  description,
  subject,
  unit,
  className,
  dueDate,
  onConfirm,
  isLoading = false,
  language,
  confirmClassName
}: HomeworkPreviewDialogProps) {
  const { t, i18n } = useTranslation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // 当对话框打开时，滚动到顶部
  useEffect(() => {
    if (open) {
      // 使用 setTimeout 确保 DOM 已经完全渲染和动画完成
      // Radix UI Dialog 的动画时长是 200ms
      const timer = setTimeout(() => {
        // 尝试多种方法确保滚动到顶部
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0
        }

        // 也尝试滚动到顶部锚点
        const topElement = document.getElementById('preview-top')
        if (topElement) {
          topElement.scrollIntoView({ behavior: 'instant', block: 'start' })
        }
      }, 250)

      return () => clearTimeout(timer)
    }
  }, [open])

  // 安装打印样式
  useEffect(() => {
    const styleId = 'homework-preview-print-styles'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        @media print {
          @page {
            size: A4 portrait;
            margin: 2cm;
          }

          /* 隐藏所有元素 */
          body * {
            visibility: hidden !important;
          }

          /* 只显示打印内容 */
          #homework-preview-content,
          #homework-preview-content * {
            visibility: visible !important;
          }

          /* 将打印内容移到左上角 - 使用 absolute 而不是 fixed */
          #homework-preview-content {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
            column-count: 1 !important;
            -webkit-column-count: 1 !important;
            -moz-column-count: 1 !important;
          }

          /* 防止所有元素使用多列布局 */
          * {
            column-count: 1 !important;
            -webkit-column-count: 1 !important;
            -moz-column-count: 1 !important;
            column-span: none !important;
            -webkit-column-span: none !important;
          }

          /* 题目避免分页截断 */
          .print-avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* 答案部分强制新页 */
          .print-page-break {
            page-break-before: always !important;
            break-before: always !important;
          }

          /* 确保数学公式显示 */
          math-field {
            display: inline-block !important;
            font-size: 14px !important;
          }

          /* 确保标题居中 */
          .text-center {
            text-align: center !important;
          }

          /* 确保flex布局正常 */
          .flex {
            display: flex !important;
          }
        }
      `
      document.head.appendChild(style)
    }

    return () => {
      const style = document.getElementById(styleId)
      if (style) {
        style.remove()
      }
    }
  }, [])

  const handlePrint = async () => {
    // Wait for i18n translations to be loaded before building print content
    await new Promise<void>((resolve) => {
      if (i18n.isInitialized && i18n.hasLoadedNamespace('translation')) {
        resolve()
        return
      }
      const check = () => {
        if (i18n.isInitialized && i18n.hasLoadedNamespace('translation')) {
          i18n.off('initialized', check)
          i18n.off('loaded', check)
          resolve()
        }
      }
      i18n.on('initialized', check)
      i18n.on('loaded', check)
      // Re-check in case translations loaded between the initial check and listener attachment
      check()
    })

    // 创建新窗口
    const printWindow = window.open('', '_blank', 'width=800,height=600')
    if (!printWindow) {
      alert(t('common.print-blocked'))
      return
    }

    // HTML-escape helper to prevent XSS and broken markup from user data
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

    // Preprocess text the same way ReadonlyQuestionDisplay does
    const splitForPrint = (text: string): string[] => {
      if (!text) return []
      const hasDisplayMath = text.includes('\\\\[') && text.includes('\\\\]')
      if (hasDisplayMath) {
        return [convertSpacesToLatex(processBackslashes(text).trim())]
      }
      return text.split(/\n/)
        .map(part => convertSpacesToLatex(processBackslashes(part.replace(/\s+$/, ''))))
        .filter(part => part.length > 0 && part.trim() !== '\\[' && part.trim() !== '\\]')
    }
    const renderMathFields = (text: string) =>
      splitForPrint(text).map(part =>
        `<math-field read-only style="border:none;background-color:transparent;padding:0;margin:0;min-height:0;line-height:1.4;display:block;">${part}</math-field>`
      ).join('')

    // 构建打印内容HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${esc(title || '课业')}</title>
          <link rel="stylesheet" href="https://unpkg.com/mathlive/dist/mathlive-static.css">
          <script src="https://cdn.jsdelivr.net/npm/mathlive"></script>
          <style>
            @page {
              size: A4 portrait;
              margin: 2cm;
            }

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
              font-size: 14px;
              line-height: 1.6;
              color: #000;
              background: white;
            }

            .text-center {
              text-align: center;
            }

            .text-2xl {
              font-size: 1.5rem;
              line-height: 2rem;
            }

            .text-xl {
              font-size: 1.25rem;
              line-height: 1.75rem;
            }

            .text-base {
              font-size: 1rem;
              line-height: 1.5rem;
            }

            .text-sm {
              font-size: 0.875rem;
              line-height: 1.25rem;
            }

            .font-bold {
              font-weight: 700;
            }

            .font-semibold {
              font-weight: 600;
            }

            .font-medium {
              font-weight: 500;
            }

            .text-gray-600 {
              color: #4b5563;
            }

            .text-gray-500 {
              color: #6b7280;
            }

            .text-gray-400 {
              color: #9ca3af;
            }

            .border-b-2 {
              border-bottom: 2px solid #d1d5db;
            }

            .border-t {
              border-top: 1px solid #d1d5db;
            }

            .border-gray-300 {
              border-color: #d1d5db;
            }

            .pb-4 {
              padding-bottom: 1rem;
            }

            .pt-2 {
              padding-top: 0.5rem;
            }

            .pt-8 {
              padding-top: 2rem;
            }

            .mb-6 {
              margin-bottom: 1.5rem;
            }

            .mt-1 {
              margin-top: 0.25rem;
            }

            .mt-2 {
              margin-top: 0.5rem;
            }

            .mt-12 {
              margin-top: 3rem;
            }

            .space-y-2 > * + * {
              margin-top: 0.5rem;
            }

            .space-y-6 > * + * {
              margin-top: 1.5rem;
            }

            .space-y-1-5 > * + * {
              margin-top: 0.375rem;
            }

            .flex {
              display: flex;
            }

            .gap-2 {
              gap: 0.5rem;
            }

            .gap-1 {
              gap: 0.25rem;
            }

            .justify-end {
              justify-content: flex-end;
            }

            .pl-8 {
              padding-left: 2rem;
            }

            .min-h-60 {
              min-height: 60px;
            }

            .min-w-1-5 {
              min-width: 1.5rem;
            }

            .min-w-2 {
              min-width: 2rem;
            }

            .shrink-0 {
              flex-shrink: 0;
            }

            .flex-1 {
              flex: 1 1 0%;
            }

            .min-w-0 {
              min-width: 0;
            }

            .print-avoid-break {
              page-break-inside: avoid;
              break-inside: avoid;
            }

            .print-page-break {
              page-break-before: always;
              break-before: always;
            }

            math-field {
              display: inline-block;
              font-size: 14px;
            }

            @media print {
              #print-toolbar {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          <div id="print-content">
            <!-- 试卷标题 -->
            <div class="text-center space-y-2 border-b-2 border-gray-300 pb-4 mb-6 print-avoid-break">
              <h1 class="text-2xl font-bold" contenteditable="true">${esc(title || '')}</h1>
              <p class="text-sm text-gray-600" contenteditable="true">${esc(description || '')}</p>

              <!-- 课业信息 -->
              <div class="text-sm text-gray-600 pt-2" style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;">
                <div><span class="font-medium">${esc(t('common.subject'))}:</span> <span contenteditable="true">${esc(subject || '')}</span></div>
                <div><span class="font-medium">${esc(t('common.unit'))}:</span> <span contenteditable="true">${esc(unit || '')}</span></div>
                <div><span class="font-medium">${esc(t('common.class'))}:</span> <span contenteditable="true">${esc(className || '')}</span></div>
                <div><span class="font-medium">${esc(t('common.due'))}:</span> <span contenteditable="true">${dueDate && dueDate instanceof Date && !isNaN(dueDate.getTime()) ? format(dueDate, 'yyyy-MM-dd HH:mm') : ''}</span></div>
              </div>

              <div class="flex justify-end text-sm text-gray-500 pt-2">
                <span>${esc(t('common.name'))}: ${mode === PreviewMode.HOMEWORK && name ? esc(name) : '_________________'}</span>
              </div>
            </div>

            <!-- 题目列表 -->
            <div class="space-y-6">
              ${questions.map((question, index) => {
                const questionText = getQuestionText(question, i18n, language)
                const isMultipleChoice = question.questionType === 'MCQ'
                const mcqData = isMultipleChoice ? parseMultipleChoiceOptions(questionText) : null

                return `
                  <div class="print-avoid-break">
                    <!-- 题号和题目 -->
                    <div class="flex gap-2">
                      <span class="font-semibold text-base shrink-0">${index + 1}.</span>
                      <div class="flex-1 min-w-0">
                        ${renderMathFields(mcqData ? mcqData.stem : questionText)}
                      </div>
                    </div>

                    ${isMultipleChoice && mcqData ? `
                      <!-- 选择题选项 -->
                      <div class="pl-8 mt-2 space-y-1-5">
                        ${mcqData.options.map(option => `
                          <div class="flex gap-2">
                            <span class="font-medium text-sm shrink-0 min-w-1-5">${option.label}.</span>
                            <div class="flex-1 min-w-0">
                              ${renderMathFields(option.content || '')}
                            </div>
                          </div>
                        `).join('')}
                      </div>
                      <!-- 答题区域 - 选择题 -->
                      <div class="pl-8 mt-2">
                        ${mode === PreviewMode.HOMEWORK && answers?.[index]?.answer ? `
                          <div class="text-sm">
                            <span class="text-gray-600">${esc(t('common.answer'))}:</span>
                            <span class="font-medium">${esc(answers[index].answer || '')}</span>
                          </div>
                        ` : `
                          <div class="text-sm text-gray-500">
                            ${esc(t('common.answer'))}: __________
                          </div>
                        `}
                      </div>
                    ` : `
                      <!-- 答题区域 - 非选择题 -->
                      <div class="pl-8 mt-2">
                        ${mode === PreviewMode.HOMEWORK && answers?.[index]?.answer ? `
                          <div class="border-t border-gray-300 pt-2 min-h-60">
                            <span class="text-sm text-gray-600">${esc(t('common.answer'))}:</span>
                              <div class="mt-1">
                                ${renderMathFields(answers[index].answer || '')}
                              </div>
                          </div>
                        ` : `
                          <div class="border-t border-gray-300 pt-2 min-h-60">
                            <span class="text-sm text-gray-400">${esc(t('common.answer'))}:</span>
                          </div>
                        `}
                      </div>
                    `}
                  </div>
                `
              }).join('')}
            </div>

            <!-- 答案部分（打印时在新页显示） - 仅在 QUIZ 模式下显示 -->
            ${mode === PreviewMode.QUIZ ? `
            <div class="print-page-break mt-12 pt-8">
              <h2 class="text-xl font-bold text-center mb-6 border-b-2 border-gray-300 pb-4">
                ${esc(t('teacher.answersheet'))}
              </h2>

              <div class="space-y-2">
                ${questions.map((question, index) => `
                  <div class="flex gap-2 print-avoid-break">
                    <span class="font-medium text-sm shrink-0 min-w-2">${index + 1}.</span>
                    <div class="flex-1 min-w-0">
                      ${renderMathFields(getStandardAnswerText(question, i18n, language))}
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
            ` : ''}
          </div>

          <div id="print-toolbar" style="position:fixed;top:0;right:0;z-index:9999;padding:12px 16px;">
            <button onclick="doPrint()" style="padding:8px 16px;font-size:14px;cursor:pointer;border:none;border-radius:6px;background:#2563eb;color:white;font-weight:600;display:flex;align-items:center;gap:6px;box-shadow:0 2px 6px rgba(37,99,235,0.3);">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/></svg>
              ${esc(t('teacher.print'))}
            </button>
          </div>

          <script>
            async function doPrint() {
              document.getElementById('print-toolbar').style.display = 'none';
              await customElements.whenDefined('math-field');
              if (document.fonts && document.fonts.ready) {
                await document.fonts.ready;
              }
              window.print();
              document.getElementById('print-toolbar').style.display = '';
            };

            // Close window after printing (Chrome, Firefox, Edge)
            window.onafterprint = function() {
              window.close();
            };
          </script>
        </body>
      </html>
    `

    // 写入内容
    printWindow.document.write(printContent)
    printWindow.document.close()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* 手机横屏(max-height:500)：整卡压缩成 ~627×277 灰底(#F3F3F4)卡,与仪表盘面板同风,对齐 Figma 570-422;
          隐藏内置空标题(>h2)与右上关闭 X(>button)。桌面/平板(高度≥768)门控不触发,原样不变。 */}
      <DialogContent className="!max-w-[900px] !w-[900px] max-h-[90vh] p-0 flex flex-col sm:!max-w-[900px] [@media(max-height:500px)]:w-[627px]! [@media(max-height:500px)]:max-w-[627px]! [@media(max-height:500px)]:h-[277px]! [@media(max-height:500px)]:max-h-[calc(100dvh-2rem)]! [@media(max-height:500px)]:rounded-[20px]! [@media(max-height:500px)]:border-0! [@media(max-height:500px)]:bg-[#F3F3F4]! [@media(max-height:500px)]:[&>h2]:hidden! [@media(max-height:500px)]:[&>button]:hidden!">
        <DialogHeader className="no-print px-6 pt-6 pb-4 border-b shrink-0 [@media(max-height:500px)]:flex-row! [@media(max-height:500px)]:items-center! [@media(max-height:500px)]:justify-between! [@media(max-height:500px)]:px-[21px]! [@media(max-height:500px)]:pt-3! [@media(max-height:500px)]:pb-2! [@media(max-height:500px)]:border-black/5!">
          <DialogTitle className="[@media(max-height:500px)]:text-xs!">{t('teacher.preview')}</DialogTitle>
          {mode === PreviewMode.HOMEWORK && name && (
            <span className="hidden [@media(max-height:500px)]:inline text-xs font-medium text-[#1A1C1D]">
              {t('common.name')}: {name}
            </span>
          )}
        </DialogHeader>

        {/* 预览内容 - 可滚动区域 */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden [@media(max-height:500px)]:[&::-webkit-scrollbar]:w-1 [@media(max-height:500px)]:[&::-webkit-scrollbar-thumb]:rounded-full [@media(max-height:500px)]:[&::-webkit-scrollbar-thumb]:bg-black/30">
          <div className="px-12 py-8 bg-background [@media(max-height:500px)]:bg-transparent! [@media(max-height:500px)]:px-[21px]! [@media(max-height:500px)]:py-3!">
            <div id="homework-preview-content" className="max-w-[210mm] mx-auto">
              {/* 顶部锚点 */}
              <div id="preview-top" />
              {/* 试卷标题(手机横屏隐藏：标题不重复,姓名已移到弹窗头部,与 Figma 570-422 一致) */}
              <div className="text-center space-y-2 border-b-2 border-gray-300 pb-4 mb-6 print-avoid-break [@media(max-height:500px)]:hidden!">
                <h1 className="text-2xl font-bold">{title}</h1>
                {description && (
                  <p className="text-sm text-gray-600">{description}</p>
                )}

                {/* 课业信息 */}
                <div className="text-sm text-gray-600 pt-2 grid grid-cols-2 gap-2">
                  {subject && (
                    <div><span className="font-medium">{t('common.subject')}:</span> {subject}</div>
                  )}
                  {unit && (
                    <div><span className="font-medium">{t('common.unit')}:</span> {unit}</div>
                  )}
                  {className && (
                    <div><span className="font-medium">{t('common.class')}:</span> {className}</div>
                  )}
                  {dueDate && dueDate instanceof Date && !isNaN(dueDate.getTime()) && (
                    <div><span className="font-medium">{t('common.due')}:</span> {format(dueDate, 'yyyy-MM-dd HH:mm')}</div>
                  )}
                </div>

                <div className="flex justify-end text-sm text-gray-500 pt-2">
                  <span>{t('common.name')}: {mode === PreviewMode.HOMEWORK && name ? name : '_________________'}</span>
                </div>
              </div>

              {/* 题目列表 */}
              <div className="space-y-6 [@media(max-height:500px)]:space-y-3!">
                {questions.map((question, index) => {
                  const questionText = getQuestionText(question, i18n, language)
                  const isMultipleChoice = question.questionType === 'MCQ'
                  const mcqData = isMultipleChoice ? parseMultipleChoiceOptions(questionText) : null

                  return (
                    <div key={question.id} className="print-avoid-break">
                      {/* 题号和题目 */}
                      <div className="flex gap-2 [@media(max-height:500px)]:gap-1!">
                        <span className="font-semibold text-base shrink-0 [@media(max-height:500px)]:text-[11px]!">
                          {index + 1}.
                        </span>
                        <div className="flex-1 min-w-0">
                          <ReadonlyQuestionDisplay
                            questionText={mcqData ? mcqData.stem : questionText}
                            className="flex flex-col gap-2 [@media(max-height:500px)]:gap-0.5!"
                            fieldClassName="[@media(max-height:500px)]:text-[11px]!"
                          />
                        </div>
                      </div>

                      {/* 选择题选项 */}
                      {isMultipleChoice && mcqData && (
                        <div className="pl-8 mt-2 space-y-1.5 [@media(max-height:500px)]:pl-5! [@media(max-height:500px)]:mt-1! [@media(max-height:500px)]:space-y-0.5!">
                          {mcqData.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="flex gap-2 [@media(max-height:500px)]:gap-1!">
                              <span className="font-medium text-sm shrink-0 min-w-[1.5rem] [@media(max-height:500px)]:text-[10px]! [@media(max-height:500px)]:min-w-[1rem]!">
                                {option.label}.
                              </span>
                              <div className="flex-1 min-w-0">
                                <math-field
                                  read-only
                                  className="w-full [@media(max-height:500px)]:text-[10px]! [@media(max-height:500px)]:min-h-0!"
                                  style={{
                                    backgroundColor: 'transparent',
                                    minHeight: '30px'
                                  }}
                                >
                                  {option.content}
                                </math-field>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 答题区域 - 选择题 */}
                      {isMultipleChoice && (
                        <div className="pl-8 mt-2 [@media(max-height:500px)]:pl-5! [@media(max-height:500px)]:mt-1!">
                          {mode === PreviewMode.HOMEWORK && answers?.[index]?.answer ? (
                            <div className="text-sm [@media(max-height:500px)]:text-[10px]!">
                              <span className="text-gray-600">{t('common.answer')}:</span>{' '}
                              <span className="font-medium">{answers[index].answer}</span>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 [@media(max-height:500px)]:text-[10px]!">
                              {t('common.answer')}: __________
                            </div>
                          )}
                        </div>
                      )}

                      {/* 答题区域 - 非选择题 */}
                      {!isMultipleChoice && (
                        <div className="pl-8 mt-2 [@media(max-height:500px)]:pl-5! [@media(max-height:500px)]:mt-1!">
                          {mode === PreviewMode.HOMEWORK && answers?.[index]?.answer ? (
                            <div className="border-t border-gray-300 pt-2 min-h-[60px] [@media(max-height:500px)]:pt-1! [@media(max-height:500px)]:min-h-[32px]!">
                              <span className="text-sm text-gray-600 [@media(max-height:500px)]:text-[10px]!">{t('common.answer')}:</span>
                              <div className="mt-1">
                                <ReadonlyQuestionDisplay
                                  questionText={answers[index].answer || ''}
                                  className="flex flex-col gap-1"
                                  fieldClassName="[@media(max-height:500px)]:text-[10px]!"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="border-t border-gray-300 pt-2 min-h-[60px] [@media(max-height:500px)]:pt-1! [@media(max-height:500px)]:min-h-[32px]!">
                              <span className="text-sm text-gray-400 [@media(max-height:500px)]:text-[10px]!">{t('common.answer')}:</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* 答案部分（打印时在新页显示） - 仅在 QUIZ 模式下显示 */}
              {mode === PreviewMode.QUIZ && (
                <div className="print-page-break mt-12 pt-8">
                  <h2 className="text-xl font-bold text-center mb-6 border-b-2 border-gray-300 pb-4">
                    {t('teacher.answersheet')}
                  </h2>

                  <div className="space-y-2">
                    {questions.map((question, index) => (
                      <div key={`answer-${question.id}`} className="flex gap-2 print-avoid-break">
                        <span className="font-medium text-sm shrink-0 min-w-[2rem]">
                          {index + 1}.
                        </span>
                        <div className="flex-1 min-w-0">
                          <ReadonlyQuestionDisplay
                            questionText={getStandardAnswerText(question, i18n, language)}
                            className="flex flex-col gap-1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 底部按钮 - 固定在底部(手机横屏:透明底+紧凑,与灰卡融合,按钮压到 h-6/text-xs 对齐 Figma) */}
        <DialogFooter className="no-print px-6 py-4 border-t bg-white gap-2 shrink-0 [@media(max-height:500px)]:px-[21px]! [@media(max-height:500px)]:py-2.5! [@media(max-height:500px)]:bg-transparent! [@media(max-height:500px)]:border-black/5!">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="[@media(max-height:500px)]:h-6! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:shadow-none!"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="outline"
            onClick={handlePrint}
            disabled={isLoading}
            className="[@media(max-height:500px)]:h-6! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:shadow-none!"
          >
            <Printer className="w-4 h-4 mr-2 [@media(max-height:500px)]:w-3! [@media(max-height:500px)]:h-3! [@media(max-height:500px)]:mr-1!" />
            {t('teacher.print')}
          </Button>
          {
            onConfirm && (
              <Button
                variant="default"
                className={`${confirmClassName || "bg-primary"} [@media(max-height:500px)]:h-6! [@media(max-height:500px)]:px-3! [@media(max-height:500px)]:text-xs! [@media(max-height:500px)]:shadow-none!`}
                onClick={() => {
                  onConfirm()
                  onOpenChange(false)
                }}
                disabled={isLoading}
              >
                {isLoading ? t('common.submitting') : (mode === PreviewMode.HOMEWORK ? t('component.submithomework') : t('teacher.confirmpublish'))}
              </Button>
            )
          }
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

